import { ArrowLeft, BookOpen, CheckCircle, Clock, Target } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getEmployees } from "../../../redux/actions/employees";
import { getCompanyStructure } from "../../../redux/actions/structure";
import { sortByPtBR } from "../../../helper/sortHelper";
import Collaborators from "../Collaborators/Collaborators";
import "./Investors.scss";

const CollaboratorsFilters = ({
  selectedDepartment,
  selectedPosition,
  searchTerm,
  pendingOnly,
  onDepartmentChange,
  onPositionChange,
  onSearchChange,
  onPendingOnlyChange,
  filteredEmployeeCount,
  departments,
  positions
}) => (
  <div className="filters-container">
    <input
      type="text"
      className="filter-input"
      placeholder="Digite para buscar..."
      value={searchTerm}
      onChange={e => onSearchChange(e.target.value)}
    />
    <select
      className="filter-select"
      value={selectedDepartment}
      onChange={e => onDepartmentChange(e.target.value)}
    >
      <option value="">Ver Todos</option>
      {departments.map(department => (
        <option key={department} value={String(department)}>{department}</option>
      ))}
    </select>
    <select
      className="filter-select"
      value={selectedPosition}
      onChange={e => onPositionChange(e.target.value)}
    >
      <option value="">Ver Todos</option>
      {positions.map(position => (
        <option key={position} value={position}>{position}</option>
      ))}
    </select>
    <button
      className={`filter-pending-toggle${pendingOnly ? ' filter-pending-toggle--active' : ''}`}
      onClick={() => onPendingOnlyChange(!pendingOnly)}
      type="button"
    >
      <Clock size={15} />
      Avaliações Pendentes
    </button>
    <span className="filter-count">{filteredEmployeeCount} funcionários selecionados</span>
  </div>
);

const Investors = () => {
  const dispatch = useDispatch();
  const metrics = useSelector((state) => state?.metrics);
  const companyMetricsEngagement = useSelector((state) => state?.company);
  const employeesMetrics = useSelector((state) => state?.employeesMetrics);
  const employeesList = useSelector((state) => state?.employees);
  const structure = useSelector((state) => state?.structure);
  const community = metrics?.communityMetrics;
  const investors = employeesList?.data?.items || [];

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedPosition, setSelectedPosition] = useState("");
  const [pendingOnly, setPendingOnly] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);

  const loadData = useCallback((page = 1) => {
    setIsLoadingEmployees(true);
    const params = {
      search: searchTerm,
      unit: selectedDepartment,
      role: selectedPosition,
      page,
      ...(pendingOnly && { pendingOnly: true }),
    };
    dispatch(getEmployees(params))
      .finally(() => setIsLoadingEmployees(false));
  }, [dispatch, searchTerm, selectedDepartment, selectedPosition, pendingOnly]);

  useEffect(() => {
    dispatch(getCompanyStructure());
  }, [dispatch]);

  useEffect(() => {
    loadData(currentPage);
  }, [loadData, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedDepartment, selectedPosition, pendingOnly]);

  const handleExitCollaborators = useCallback((shouldReload = false) => {
    setSelectedEmployee(undefined);
    if (shouldReload) {
      loadData(currentPage);
    }
  }, [loadData, currentPage]);

  const employeesPagination = employeesList?.data || {};
  const totalPages = employeesPagination.totalPages || 1;
  const totalItems = employeesPagination.totalItems || 0;
  const nextPage = employeesPagination.nextPage;
  const prevPage = employeesPagination.prevPage;

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || isLoadingEmployees) return;
    setCurrentPage(page);
  };

  const departments = Array.isArray(structure?.data)
    ? sortByPtBR([...new Set(structure.data.map(team => team.name).filter(Boolean))])
    : [];

  const positions = Array.isArray(structure?.data)
    ? sortByPtBR([...new Set(structure.data.flatMap(team =>
      Array.isArray(team.roles)
        ? team.roles.map(role => typeof role === 'string' ? role : role.name).filter(Boolean)
        : []
    ))])
    : [];

  const filteredEmployees = (employeesPagination.items || []).map((employee) => ({
    id: employee.person.id,
    name: employee.person.name,
    department: employee.unit,
    role: employee.role,
    affiliations: employee.person.metrics.affiliations ?? 0,
    taskExecutionCount: employee.person.metrics.taskExecutionCount ?? 0,
    pendingAssessmentsCount: employee.toReviewEvaluationsCount ?? 0,
  }));
  if (selectedEmployee) {
    return <Collaborators employee={selectedEmployee} exit={handleExitCollaborators} />;
  }
  return (
    <div className="investors-bg">
      <div className="investors-container">
        <div className="investors-header">
          <h1 className="investors-title">Investidores</h1>
          <p className="investors-subtitle">Visualize e gerencie detalhes de competências dos funcionários</p>
        </div>


        <CollaboratorsFilters
          selectedDepartment={selectedDepartment}
          selectedPosition={selectedPosition}
          searchTerm={searchTerm}
          pendingOnly={pendingOnly}
          onDepartmentChange={setSelectedDepartment}
          onPositionChange={setSelectedPosition}
          onSearchChange={setSearchTerm}
          onPendingOnlyChange={setPendingOnly}
          filteredEmployeeCount={totalItems}
          departments={departments}
          positions={positions}
        />


        <div className="investors-table-wrapper">
          {isLoadingEmployees ? (
            <div className="investors-loading-state">
              <div className="investors-loading-spinner">
                <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                  <g fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="2">
                    <path
                      strokeDasharray="60"
                      strokeDashoffset="60"
                      strokeOpacity="0.3"
                      d="M12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3Z"
                    >
                      <animate fill="freeze" attributeName="stroke-dashoffset" dur="1.3s" values="60;0" />
                    </path>
                    <path
                      strokeDasharray="15"
                      strokeDashoffset="15"
                      d="M12 3C16.9706 3 21 7.02944 21 12"
                    >
                      <animate fill="freeze" attributeName="stroke-dashoffset" dur="0.3s" values="15;0" />
                      <animateTransform
                        attributeName="transform"
                        dur="1.5s"
                        repeatCount="indefinite"
                        type="rotate"
                        values="0 12 12;360 12 12"
                      />
                    </path>
                  </g>
                </svg>
              </div>
              <p>Carregando investidores...</p>
            </div>
          ) : (
          <div className="investors-table-scroll">
            <table className="investors-table">
              <thead>
                <tr>
                  <th>Nome do Funcionário</th>
                  <th>Departamento</th>
                  <th>Cargo</th>
                  <th>Competências</th>
                  <th>Conteúdos</th>
                  <th>Avaliações Pendentes</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((employee) => (
                  <tr
                    key={employee.id}
                    className="investors-table-row"
                    onClick={() => setSelectedEmployee(employee)}
                  >
                    <td>
                      <div className="investors-table-name">{employee.name}</div>
                    </td>
                    <td>
                      <span className="investors-table-department">{employee.department}</span>
                    </td>
                    <td>
                      <span className="investors-table-role">{employee.role}</span>
                    </td>
                    <td>
                      <div className="investors-table-icon-group">
                        <Target size={16} className="icon-blue" />
                        <span className="investors-table-number">{employee.affiliations}</span>
                      </div>
                    </td>
                    <td>
                      <div className="investors-table-icon-group">
                        <BookOpen size={16} className="icon-green" />
                        <span className="investors-table-number">{employee.taskExecutionCount}</span>
                      </div>
                    </td>
                    <td>
                      <div className="investors-table-icon-group">
                        {employee.pendingAssessmentsCount > 0 ? (
                          <>
                            <Clock size={16} className="icon-orange" />
                            <span className="investors-table-number icon-orange">{employee.pendingAssessmentsCount}</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle size={16} className="icon-green" />
                            <span className="investors-table-number icon-green">0</span>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="collaborators-pagination">
            <div className="pagination-info">
              <div className="page-indicator">
                <span>Página</span>
                <span className="page-number">{currentPage}</span>
                <span className="total-pages">de {totalPages}</span>
              </div>
              <div className="items-count">
                <span className="items-number">{totalItems}</span>
                <span>funcionários no total</span>
              </div>
            </div>
            <div className="pagination-controls">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!prevPage || isLoadingEmployees}
                className="pagination-btn"
              >
                <ArrowLeft className="btn-icon" />
                <span>Anterior</span>
              </button>

              <div className="page-separator"></div>

              <div className="current-page-display">
                {currentPage}
              </div>

              <div className="page-separator"></div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!nextPage || isLoadingEmployees}
                className="pagination-btn"
              >
                <span>Próxima</span>
                <ArrowLeft className="btn-icon" style={{ transform: 'rotate(180deg)' }} />
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Investors;
